import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { BarChart3, Globe, Mail, Phone, Building2, MapPin, CalendarCheck, CheckCircle2, ArrowRight, Zap } from "lucide-react";
import { GOOGLE_BUSINESS_CATEGORIES } from "@/lib/categories";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORIES = [...GOOGLE_BUSINESS_CATEGORIES, "Other"];

const BENEFITS = [
  "Free, no-obligation digital audit",
  "Personalized growth recommendations",
  "A clear plan to win more customers online",
];

export default function LeadFormPage() {
  const [params] = useSearchParams();
  const formId = params.get("form_id") || "";
  const embed = params.get("embed") === "1";
  const preview = params.get("preview") === "1";
  const [config, setConfig] = useState(preview ? { theme: "light", layout: "centered", primary_color: "#4e3c97", form_title: "Free Digital Audit", form_description: "Get a comprehensive audit of your online presence" } : null);
  const [loading, setLoading] = useState(!preview);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [bookingUrl, setBookingUrl] = useState("");
  const [error, setError] = useState("");

  const [form, setForm] = useState({
    business_name: "", website: "", phone: "", email: "",
    city: "", state: "", category: "",
  });

  useEffect(() => {
    if (preview) return;  // preview mode receives config via postMessage
    if (!formId) { setLoading(false); setError("Invalid form link."); return; }
    fetch(`${API}/public/form/${formId}`)
      .then((r) => { if (!r.ok) throw new Error("Not found"); return r.json(); })
      .then((data) => { setConfig(data); setLoading(false); })
      .catch(() => { setError("This form is not available."); setLoading(false); });
  }, [formId, preview]);

  // Live preview: receive draft config from the editor (same-origin only)
  useEffect(() => {
    if (!preview) return;
    const onMsg = (e) => {
      // SECURITY: only trust messages from our own editor (same origin),
      // never from an arbitrary embedding site.
      if (e.origin !== window.location.origin) return;
      if (e.data && e.data.type === "auditpro-preview-config") setConfig(e.data.config);
    };
    window.addEventListener("message", onMsg);
    try { window.parent.postMessage({ type: "auditpro-preview-ready" }, window.location.origin); }
    catch (err) { console.debug("preview-ready postMessage failed", err); }
    return () => window.removeEventListener("message", onMsg);
  }, [preview]);

  // When embedded/previewed, override the document background so the iframe is truly
  // transparent (the app's <body> is white by default, which otherwise leaks through).
  useEffect(() => {
    if (!(embed || preview)) return;
    const isDark = config?.theme === "dark";
    const themeDefaultBg = isDark ? "#020617" : "#f8fafc";
    const bg = config?.background_color
      ? (config.background_color === "transparent" ? "transparent" : config.background_color)
      : (embed ? "transparent" : themeDefaultBg);
    const root = document.getElementById("root");
    const prev = {
      html: document.documentElement.style.background,
      body: document.body.style.background,
      root: root ? root.style.background : "",
    };
    document.documentElement.style.background = bg;
    document.body.style.background = bg;
    if (root) root.style.background = bg;
    return () => {
      document.documentElement.style.background = prev.html;
      document.body.style.background = prev.body;
      if (root) root.style.background = prev.root;
    };
  }, [config, embed, preview]);

  // In embed mode, continuously report content height so the host iframe can auto-resize
  useEffect(() => {
    if (!embed) return;
    const post = () => {
      try {
        // Use the largest reliable measure so late layout shifts (logo/font load,
        // validation errors) never leave the form clipped.
        const h = Math.max(
          document.body.scrollHeight,
          document.documentElement.scrollHeight,
          document.body.offsetHeight
        );
        // Target '*' is required: this widget is embedded on arbitrary client
        // sites whose origin we can't know. Only non-sensitive layout data
        // (height + formId) is sent — never user/PII data.
        window.parent.postMessage({ type: "auditpro-resize", formId, height: h }, "*");
      } catch (err) { console.debug("resize postMessage failed", err); }
    };
    post();
    const ro = new ResizeObserver(post);
    ro.observe(document.body);
    window.addEventListener("load", post);
    // Re-post a few times to catch async layout shifts (logo image / web fonts).
    const timers = [150, 400, 900, 1800].map((ms) => setTimeout(post, ms));
    return () => {
      ro.disconnect();
      window.removeEventListener("load", post);
      timers.forEach(clearTimeout);
    };
  }, [embed, formId, loading, submitted, error, config]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.business_name || !form.email) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/public/lead-form`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ form_id: formId, ...form }),
      });
      if (res.ok) {
        const data = await res.json();
        setBookingUrl(data.booking_url || "");
        setSubmitted(true);
      } else {
        setError("Something went wrong. Please try again.");
      }
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const primaryColor = config?.primary_color || "#4e3c97";
  const theme = config?.theme === "dark" ? "dark" : "light";
  const layout = config?.layout || "centered";
  const dark = theme === "dark";
  const formWidth = config?.form_width || 520;

  // theme tokens
  const t = dark
    ? { card: "bg-slate-900 border border-slate-800", text: "text-white", muted: "text-slate-400", divider: "border-slate-800" }
    : { card: "bg-white", text: "text-slate-900", muted: "text-slate-500", divider: "border-slate-200" };
  const inputCls = dark
    ? "pl-10 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
    : "pl-10";
  const inputClsNoIcon = dark
    ? "bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
    : "";

  // Background: blend transparently when embedded; use the configured/theme color otherwise
  const fit = embed || preview;  // render to content size, not full viewport
  const themeDefaultBg = dark ? "#020617" : "#f8fafc";
  const bgColor = config?.background_color
    ? (config.background_color === "transparent" ? "transparent" : config.background_color)
    : (embed ? "transparent" : themeDefaultBg);
  const wrapperCls = fit
    ? "flex justify-center items-start p-3"
    : "min-h-screen flex items-center justify-center p-4";
  const wrapperStyle = { backgroundColor: bgColor, minHeight: fit ? undefined : "100vh" };

  // Card style: full card (shadow), flat (border only), or transparent (fields sit on the page)
  const cardStyle = config?.card_style || "card";
  const cardCls =
    cardStyle === "transparent" ? "bg-transparent shadow-none border-0"
    : cardStyle === "flat" ? `${t.card} shadow-none border ${t.divider}`
    : `${t.card} shadow-xl border-0`;

  if (loading) {
    return (
      <div className={wrapperCls} style={wrapperStyle}>
        <div className="w-6 h-6 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error && !config) {
    return (
      <div className={wrapperCls} style={wrapperStyle}>
        <Card className="max-w-md w-full mx-4"><CardContent className="py-12 text-center">
          <p className="text-muted-foreground">{error}</p>
        </CardContent></Card>
      </div>
    );
  }

  // Brand mark — logo image if provided, else colored icon tile
  const renderBrandMark = (size = 12) => (
    config?.logo_url ? (
      <img src={config.logo_url} alt="logo" data-testid="form-logo" className="mx-auto object-contain rounded-lg" style={{ height: size * 4, maxWidth: 180 }} />
    ) : (
      <div className="rounded-xl mx-auto flex items-center justify-center" style={{ backgroundColor: `${primaryColor}20`, width: size * 4, height: size * 4 }}>
        <BarChart3 className="w-6 h-6" style={{ color: primaryColor }} />
      </div>
    )
  );

  // Thank You / Book Appointment screen
  if (submitted) {
    return (
      <div className={wrapperCls} style={wrapperStyle}>
        <Card className={`w-full ${cardCls}`} style={{ maxWidth: formWidth }}>
          <CardContent className="py-12 px-8 text-center space-y-6">
            <div className="w-16 h-16 rounded-full mx-auto flex items-center justify-center" style={{ backgroundColor: `${primaryColor}20` }}>
              <CheckCircle2 className="w-8 h-8" style={{ color: primaryColor }} />
            </div>
            <div>
              <h1 className={`text-2xl font-bold tracking-tight ${t.text}`} style={{ fontFamily: 'Manrope, sans-serif' }}>
                Your Audit is Being Prepared
              </h1>
              <p className={`${t.muted} mt-2 text-sm leading-relaxed`}>
                We're running a comprehensive digital audit of your business. Our team will review the results
                and prepare personalized recommendations for you.
              </p>
            </div>

            <div className="p-4 rounded-lg border-2 border-dashed" style={{ borderColor: `${primaryColor}40`, backgroundColor: `${primaryColor}10` }}>
              <CalendarCheck className="w-6 h-6 mx-auto mb-2" style={{ color: primaryColor }} />
              <h2 className={`text-base font-semibold mb-1 ${t.text}`}>Book Your Results Review</h2>
              <p className={`text-xs ${t.muted} mb-4`}>
                Schedule a free consultation with our team to walk through your audit results
                and discuss how we can help grow your business online.
              </p>
              {bookingUrl ? (
                <a href={bookingUrl} target="_blank" rel="noopener noreferrer">
                  <Button data-testid="book-appointment-btn" className="text-white font-semibold px-8 py-2" style={{ backgroundColor: primaryColor }}>
                    <CalendarCheck className="w-4 h-4 mr-2" /> Book Free Consultation <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </a>
              ) : (
                <p className={`text-xs ${t.muted} italic`}>Our team will reach out to you via email to schedule a review.</p>
              )}
            </div>

            {config?.company_name && (
              <p className={`text-[11px] ${t.muted}`}>Powered by {config.company_name}</p>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // The form fields (shared across all layouts)
  const renderFormBody = (compact = false) => (
    <form onSubmit={handleSubmit} className={compact ? "space-y-3" : "space-y-4"}>
      <div>
        <Label className={`text-xs font-semibold ${t.text}`}>Business Name *</Label>
        <div className="relative mt-1">
          <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input data-testid="lead-business-name" placeholder="Your Business Name" value={form.business_name}
            onChange={(e) => setForm({ ...form, business_name: e.target.value })} className={inputCls} required />
        </div>
      </div>

      <div>
        <Label className={`text-xs font-semibold ${t.text}`}>Website URL</Label>
        <div className="relative mt-1">
          <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input data-testid="lead-website" placeholder="yourwebsite.com" value={form.website}
            onChange={(e) => setForm({ ...form, website: e.target.value })} className={inputCls} />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className={`text-xs font-semibold ${t.text}`}>Email *</Label>
          <div className="relative mt-1">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input data-testid="lead-email" type="email" placeholder="you@email.com" value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} required />
          </div>
        </div>
        <div>
          <Label className={`text-xs font-semibold ${t.text}`}>Phone</Label>
          <div className="relative mt-1">
            <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input data-testid="lead-phone" placeholder="(555) 123-4567" value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div>
          <Label className={`text-xs font-semibold ${t.text}`}>City</Label>
          <div className="relative mt-1">
            <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input data-testid="lead-city" placeholder="City" value={form.city}
              onChange={(e) => setForm({ ...form, city: e.target.value })} className={inputCls} />
          </div>
        </div>
        <div>
          <Label className={`text-xs font-semibold ${t.text}`}>State</Label>
          <Input data-testid="lead-state" placeholder="TX" value={form.state}
            onChange={(e) => setForm({ ...form, state: e.target.value })} className={`mt-1 ${inputClsNoIcon}`} />
        </div>
        <div>
          <Label className={`text-xs font-semibold ${t.text}`}>Industry</Label>
          <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
            <SelectTrigger data-testid="lead-category" className={`mt-1 ${inputClsNoIcon}`}><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent className="max-h-72">
              {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {error && <p className="text-xs text-rose-500">{error}</p>}

      <Button data-testid="lead-submit-btn" type="submit" disabled={submitting || !form.business_name || !form.email}
        className="w-full text-white font-semibold py-2.5" style={{ backgroundColor: primaryColor }}>
        {submitting ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" /> : <Zap className="w-4 h-4 mr-2" />}
        {submitting ? "Submitting..." : "Get My Free Audit"}
      </Button>

      <p className={`text-[10px] text-center ${t.muted}`}>
        Your information is secure and will only be used to prepare your audit report.
      </p>
    </form>
  );

  const renderHeader = (logoSize = 12, compact = false) => (
    <div className="text-center mb-6">
      <div className="mb-3">{renderBrandMark(logoSize)}</div>
      <h1 className={`${compact ? "text-lg" : "text-xl"} font-bold tracking-tight ${t.text}`} style={{ fontFamily: 'Manrope, sans-serif' }}>
        {config?.form_title || "Free Digital Audit"}
      </h1>
      <p className={`text-sm ${t.muted} mt-1`}>
        {config?.form_description || "Get a comprehensive audit of your online presence"}
      </p>
    </div>
  );

  // ── SPLIT LAYOUT ──
  if (layout === "split") {
    return (
      <div className={wrapperCls} style={wrapperStyle}>
        <Card data-testid="lead-form-card" className={`w-full overflow-hidden ${cardStyle === "transparent" ? "bg-transparent shadow-none border-0" : `${t.card} shadow-2xl border-0`}`} style={{ maxWidth: Math.max(formWidth + 340, 820) }}>
          <div className="grid md:grid-cols-2">
            {/* Brand panel */}
            <div className="p-8 flex flex-col justify-center text-white" style={{ backgroundColor: primaryColor }}>
              {config?.logo_url
                ? <img src={config.logo_url} alt="logo" data-testid="form-logo" className="object-contain mb-5" style={{ height: 44, maxWidth: 180 }} />
                : <BarChart3 className="w-10 h-10 mb-5" />}
              <h1 className="text-2xl font-bold tracking-tight mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {config?.form_title || "Free Digital Audit"}
              </h1>
              <p className="text-sm text-white/85 mb-6">
                {config?.form_description || "Get a comprehensive audit of your online presence"}
              </p>
              <ul className="space-y-2.5">
                {BENEFITS.map((b) => (
                  <li key={b} className="flex items-start gap-2 text-sm text-white/90">
                    <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" /> {b}
                  </li>
                ))}
              </ul>
              {config?.company_name && (
                <p className="text-[11px] text-white/70 mt-8">Powered by {config.company_name}</p>
              )}
            </div>
            {/* Form panel */}
            <div className="p-8">
              {renderFormBody()}
            </div>
          </div>
        </Card>
      </div>
    );
  }

  // ── CENTERED / COMPACT LAYOUTS ──
  const compact = layout === "compact";
  return (
    <div className={wrapperCls} style={wrapperStyle}>
      <Card data-testid="lead-form-card" className={`w-full ${cardCls}`} style={{ maxWidth: formWidth }}>
        <CardContent className={compact ? "py-6 px-6" : "py-8 px-8"}>
          {renderHeader(compact ? 10 : 12, compact)}
          {renderFormBody(compact)}
          {config?.company_name && (
            <p className={`text-[10px] text-center ${t.muted} mt-4 pt-4 border-t ${t.divider}`}>
              Powered by {config.company_name}
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
